import Close from "@mui/icons-material/Close";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import type { SvgIconProps } from "@mui/material/SvgIcon";
import type { ComponentType, ReactNode } from "react";
import { forwardRef, useCallback, useLayoutEffect, useRef, useState } from "react";

import BooleanIcon from "@/components/global/components/icons/BooleanIcon";
import DateIcon from "@/components/global/components/icons/DateIcon";
import DatetimeIcon from "@/components/global/components/icons/DatetimeIcon";
import DecimalIcon from "@/components/global/components/icons/DecimalIcon";
import NumberIcon from "@/components/global/components/icons/NumberIcon";
import TextIcon from "@/components/global/components/icons/TextIcon";
import TimeIcon from "@/components/global/components/icons/TimeIcon";
import type { DataTypeIconKey } from "@/constants/rule-variables";

export const DATA_TYPE_PILL_BG = "rgba(2, 213, 171, 0.1)";
export const DATA_TYPE_PILL_ON_SURFACE = "#1d192b";

const PILL_LABEL_SX = {
	fontSize: 14,
	fontWeight: 500,
	lineHeight: "20px",
	letterSpacing: "0.1px",
	color: DATA_TYPE_PILL_ON_SURFACE,
	whiteSpace: "nowrap",
} as const;

const DATA_TYPE_SVG_ICONS: Record<DataTypeIconKey, ComponentType<SvgIconProps>> = {
	boolean: BooleanIcon,
	number: NumberIcon,
	text: TextIcon,
	decimal: DecimalIcon,
	date: DateIcon,
	datetime: DatetimeIcon,
	time: TimeIcon,
};

function DataTypePillSvgIcon({ iconKey, sx, ...rest }: SvgIconProps & { iconKey: DataTypeIconKey }) {
	const Cmp = DATA_TYPE_SVG_ICONS[iconKey];
	return <Cmp {...rest} sx={{ fontSize: 18, flexShrink: 0, ...sx }} />;
}

/** Fallback label when JS measurement chooses binary truncate (tests / helpers). */
export function resolveCrossLocationPillDisplayLabel(
	fullLabel: string,
	fieldName: string,
	fits: boolean,
): string {
	if (fits) return fullLabel;
	const plainFieldName = fieldName.trim();
	return plainFieldName ? `...${plainFieldName}` : fullLabel;
}

const LeadingEllipsisPillLabel = forwardRef<HTMLElement, { label: string }>(
	function LeadingEllipsisPillLabel({ label }, ref) {
		return (
			<Typography
				ref={ref}
				component="span"
				sx={{
					...PILL_LABEL_SX,
					display: "block",
					overflow: "hidden",
					textOverflow: "ellipsis",
					minWidth: 0,
					width: "100%",
					direction: "rtl",
					textAlign: "left",
				}}
			>
				<Box component="span" sx={{ direction: "ltr", unicodeBidi: "embed" }}>
					{label}
				</Box>
			</Typography>
		);
	},
);

function PillLabel({
	label,
	truncateStart,
	tooltipTitle,
}: {
	label: string | ReactNode;
	truncateStart?: boolean;
	tooltipTitle?: string;
}) {
	const textRef = useRef<HTMLElement>(null);
	const [isTruncated, setIsTruncated] = useState(false);
	const isStringLabel = typeof label === "string";

	const measure = useCallback(() => {
		const el = textRef.current;
		if (!el) return;
		setIsTruncated(el.scrollWidth > el.clientWidth + 1);
	}, []);

	useLayoutEffect(() => {
		if (!isStringLabel) return;
		measure();
		const el = textRef.current;
		const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
		if (ro && el) ro.observe(el);
		window.addEventListener("resize", measure);
		return () => {
			ro?.disconnect();
			window.removeEventListener("resize", measure);
		};
	}, [measure, label, truncateStart, isStringLabel]);

	if (!isStringLabel) {
		return (
			<Box
				component="span"
				sx={{
					display: "inline-flex",
					alignItems: "center",
					flex: "1 1 0",
					minWidth: 0,
					overflow: "hidden",
				}}
			>
				{label}
			</Box>
		);
	}

	const text = truncateStart ? (
		<LeadingEllipsisPillLabel ref={textRef} label={label} />
	) : (
		<Typography
			ref={textRef}
			component="span"
			sx={{
				...PILL_LABEL_SX,
				overflow: "hidden",
				textOverflow: "ellipsis",
				minWidth: 0,
				width: "100%",
				display: "block",
			}}
		>
			{label}
		</Typography>
	);

	const fullTitle = tooltipTitle ?? (isStringLabel ? label : "");

	return (
		<Tooltip
			title={isTruncated || (!isStringLabel && fullTitle) ? fullTitle : ""}
			enterDelay={300}
			slotProps={{
				popper: { sx: { maxWidth: 480 } },
			}}
		>
			<Box
				component="span"
				sx={{
					display: "flex",
					flex: "1 1 0",
					minWidth: 0,
					width: "100%",
					overflow: "hidden",
				}}
			>
				{text}
			</Box>
		</Tooltip>
	);
}

type DataTypeSelectedPillBaseProps = {
	label: string | ReactNode;
	onClear: () => void;
	clearAriaLabel: string;
	truncateStart?: boolean;
	truncateFieldName?: string;
	tooltipTitle?: string;
};

export type DataTypeSelectedPillProps = DataTypeSelectedPillBaseProps &
	(
		| { iconKey: DataTypeIconKey; leadingIcon?: never }
		| { leadingIcon: ReactNode; iconKey?: never }
	);

export function DataTypeSelectedPill(props: DataTypeSelectedPillProps) {
	const {
		label,
		onClear,
		clearAriaLabel,
		iconKey,
		leadingIcon,
		truncateStart,
		tooltipTitle,
	} = props;

	return (
		<Box
			data-testid="entity-selected-pill"
			onMouseDown={(e) => e.stopPropagation()}
			sx={{
				display: "flex",
				alignItems: "center",
				alignSelf: "stretch",
				gap: 1,
				px: 1,
				py: "6px",
				borderRadius: "8px",
				backgroundColor: DATA_TYPE_PILL_BG,
				marginRight: 0,
				maxWidth: "100%",
				minWidth: 0,
				width: "100%",
				flex: "1 1 0",
				boxSizing: "border-box",
			}}
		>
			{leadingIcon ??
				(iconKey != null ? <DataTypePillSvgIcon iconKey={iconKey} aria-hidden /> : null)}
			<PillLabel label={label} truncateStart={truncateStart} tooltipTitle={tooltipTitle} />
			<IconButton
				size="small"
				aria-label={clearAriaLabel}
				onMouseDown={(e) => {
					// Keep the TextField/Autocomplete from blurring before click; without this the click
					// can be dropped and onClear never runs (common with adornments inside inputs).
					e.preventDefault();
					e.stopPropagation();
				}}
				onClick={(e) => {
					e.stopPropagation();
					onClear();
				}}
				sx={{
					p: "2px",
					color: DATA_TYPE_PILL_ON_SURFACE,
					flexShrink: 0,
					"&:hover": { backgroundColor: "rgba(29, 25, 43, 0.08)" },
				}}
			>
				<Close sx={{ fontSize: 18 }} />
			</IconButton>
		</Box>
	);
}
